// Lanzador del modo "Ver como" (suplantación de solo lectura).
//
// Modal con el ROSTER REAL del tenant: el directivo elige un grupo de rol
// (Coordinadores / Supervisores / Líderes / Mantenimiento) y una PERSONA. Al
// confirmar entra al mundo real de esa persona (su home, su sidebar, sus datos)
// en solo lectura. El backend hace la suplantación por `user_id`.
//
// Al entrar/salir se limpia la cache de TanStack Query: TODO debe refetchearse
// con (o sin) el header X-Ver-Como, si no quedarían datos del rol anterior.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { VerComoRoster } from 'schemas';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { SegmentedControl } from '../../../components/ui/segmented-control';
import { Spinner } from '../../../components/ui/spinner';
import { registrarVerComoInicio, useVerComoRoster } from '../../../lib/api/queries';
import { homeVerComo, useVerComo, type DestinoVerComo } from '../../../lib/ver-como';

type Grupo = 'coordinador' | 'supervisor' | 'lider' | 'mantenimiento';

const GRUPOS: { value: Grupo; label: string }[] = [
  { value: 'coordinador', label: 'Coordinadores' },
  { value: 'supervisor', label: 'Supervisores' },
  { value: 'lider', label: 'Líderes' },
  { value: 'mantenimiento', label: 'Mantenimiento' },
];

const ROL_MTTO_LABEL: Record<string, string> = {
  tecnico: 'Técnico',
  coordinador_mtto: 'Coordinador de mtto',
};

interface Persona {
  userId: string;
  nombre: string;
  destino: DestinoVerComo;
  plantaLabel: string | null;
  detalle: string | null;
  /** ¿Programado/activo hoy? (líder/supervisor). Coordinador/mtto: siempre true. */
  activoHoy: boolean;
  /** ¿El concepto "activo hoy" aplica a este grupo? (solo líder/supervisor). */
  aplicaActividad: boolean;
}

/** ¿El grupo tiene noción de "programado hoy"? (líder y supervisor). */
function grupoUsaActividad(grupo: Grupo): boolean {
  return grupo === 'lider' || grupo === 'supervisor';
}

/** Aplana el roster del grupo elegido a una lista uniforme de personas. */
function personasDelGrupo(roster: VerComoRoster | undefined, grupo: Grupo): Persona[] {
  if (!roster) return [];
  if (grupo === 'mantenimiento') {
    return roster.mantenimiento.map((m) => ({
      userId: m.user_id,
      nombre: `${m.nombre} ${m.apellido}`.trim(),
      destino: 'mantenimiento',
      plantaLabel: m.planta,
      detalle: ROL_MTTO_LABEL[m.rol_mtto] ?? m.rol_mtto,
      activoHoy: true,
      aplicaActividad: false,
    }));
  }
  const lista =
    grupo === 'coordinador'
      ? roster.coordinadores
      : grupo === 'supervisor'
        ? roster.supervisores
        : roster.lideres;
  const aplicaActividad = grupoUsaActividad(grupo);
  return lista.map((u) => ({
    userId: u.user_id,
    nombre: `${u.nombre} ${u.apellido}`.trim(),
    destino: grupo,
    plantaLabel: u.plantas.length ? u.plantas.join(', ') : null,
    // Para líder/supervisor: el detalle es su programación de hoy (línea·turno).
    detalle: aplicaActividad ? u.detalle_hoy : null,
    activoHoy: u.activo_hoy,
    aplicaActividad,
  }));
}

export function VerComoLanzador() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mx-2 my-1 flex items-center gap-2 rounded border border-border bg-surface-elevated px-3 py-2 text-left text-xs font-medium text-fg transition-colors hover:bg-surface-overlay"
      >
        <span aria-hidden>👁</span>
        Ver como…
      </button>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <VerComoModalContent open={open} onClose={() => setOpen(false)} />
      </Dialog>
    </>
  );
}

export function VerComoModalContent({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { entrar } = useVerComo();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const roster = useVerComoRoster(open);

  const [grupo, setGrupo] = useState<Grupo>('coordinador');
  const [busqueda, setBusqueda] = useState('');
  // Para líder/supervisor, por defecto solo mostramos a los programados hoy:
  // suplantar a alguien sin programación cae en una pantalla vacía ("sin
  // asignación hoy"), que no sirve.
  const [soloActivos, setSoloActivos] = useState(true);

  const usaActividad = grupoUsaActividad(grupo);

  const personas = useMemo(() => {
    let todas = personasDelGrupo(roster.data, grupo);
    const q = busqueda.trim().toLowerCase();
    if (q) {
      todas = todas.filter(
        (p) =>
          p.nombre.toLowerCase().includes(q) || (p.plantaLabel?.toLowerCase().includes(q) ?? false),
      );
    }
    if (usaActividad && soloActivos) {
      todas = todas.filter((p) => p.activoHoy);
    }
    // Activos primero (estable respecto del orden alfabético del backend).
    return [...todas].sort((a, b) => Number(b.activoHoy) - Number(a.activoHoy));
  }, [roster.data, grupo, busqueda, usaActividad, soloActivos]);

  function elegir(p: Persona) {
    entrar({
      targetUserId: p.userId,
      destino: p.destino,
      nombre: p.nombre,
      plantaLabel: p.plantaLabel,
    });
    // Refetch total con el nuevo contexto de suplantación.
    queryClient.clear();
    // Auditoría best-effort (no bloquea la experiencia).
    void registrarVerComoInicio(p.userId).catch(() => {});
    onClose();
    navigate(homeVerComo(p.destino));
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Ver como</DialogTitle>
        <DialogDescription>
          Elija una persona para ver su plataforma tal cual la ve ella. Es solo lectura: no puede
          editar ni mover nada.
        </DialogDescription>
      </DialogHeader>
      <DialogContent className="flex flex-col gap-3">
        <SegmentedControl<Grupo>
          value={grupo}
          onChange={(g) => {
            setGrupo(g);
            setBusqueda('');
          }}
          options={GRUPOS}
          size="sm"
          className="flex-wrap"
        />
        <Input
          placeholder="Buscar persona o planta…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
        {usaActividad ? (
          <label className="flex items-center gap-2 text-xs text-fg-secondary">
            <input
              type="checkbox"
              checked={soloActivos}
              onChange={(e) => setSoloActivos(e.target.checked)}
              className="size-3.5 accent-accent"
            />
            Solo programados hoy
            <span className="text-fg-tertiary">
              (suplantar a alguien sin programación cae en una pantalla vacía)
            </span>
          </label>
        ) : null}
        <div className="max-h-72 overflow-y-auto rounded border border-border">
          {roster.isLoading ? (
            <div className="flex items-center gap-2 px-3 py-6 text-sm text-fg-secondary">
              <Spinner className="size-4" /> Cargando personas…
            </div>
          ) : roster.isError ? (
            <p className="px-3 py-6 text-sm text-crit">No pudimos cargar el listado.</p>
          ) : personas.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-fg-tertiary">
              {usaActividad && soloActivos
                ? 'Nadie programado hoy en este rol. Destildá “Solo programados hoy” para ver a todos.'
                : 'Sin personas en este grupo.'}
            </p>
          ) : (
            <ul className="divide-y divide-border/60">
              {personas.map((p) => {
                // Estado de actividad (solo líder/supervisor).
                const sinProg = p.aplicaActividad && !p.activoHoy;
                const sub = sinProg
                  ? ['Sin programación hoy', p.plantaLabel].filter(Boolean).join(' · ')
                  : [p.detalle, p.plantaLabel ?? 'todas las plantas'].filter(Boolean).join(' · ');
                return (
                  <li key={p.userId}>
                    <button
                      type="button"
                      onClick={() => elegir(p)}
                      className="flex w-full items-start gap-2 px-3 py-2.5 text-left transition-colors hover:bg-surface-overlay"
                    >
                      {p.aplicaActividad ? (
                        <span
                          aria-hidden
                          className={`mt-1.5 inline-flex size-2 shrink-0 rounded-full ${
                            p.activoHoy ? 'bg-ok' : 'bg-fg-tertiary/40'
                          }`}
                        />
                      ) : null}
                      <span className="flex min-w-0 flex-col gap-0.5">
                        <span className="text-sm font-medium text-fg">{p.nombre}</span>
                        <span className={`text-xs ${sinProg ? 'text-warn' : 'text-fg-tertiary'}`}>
                          {sub}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
      <DialogFooter>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancelar
        </Button>
      </DialogFooter>
    </>
  );
}
